import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip } from "recharts";
import { LogoMark } from "./Logo";
import { PrintOverlay } from "./printDocs";
import { fmtTon } from "@/lib/utils";

const kg = (n) => (n != null ? Number(n).toLocaleString("id-ID", { maximumFractionDigits: 1 }) : "—");
const dt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const MONTH_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const monthLabel = (ym) => `${MONTH_ID[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

const Cell = ({ children, cls = "", ...rest }) => <td className={`border border-gray-400 px-1.5 py-0.5 ${cls}`} {...rest}>{children}</td>;
const Head = ({ cols }) => (
  <thead><tr className="bg-gray-200">{cols.map((c) => <th key={c} className="border border-gray-400 px-1.5 py-0.5 text-left font-bold">{c}</th>)}</tr></thead>
);

export function ProjectReportDoc({ project, curve, spks, items, companyName, only, filterLabel }) {
  const stats = curve?.ok ? curve : null;
  let accAct = 0;
  const monthTable = (stats?.months || []).map((m) => { accAct += m.actual; return { ...m, accPlan: m.planCum, accAct }; });
  const showCurve = !only || only === "curve";
  const showTable = !only || only === "table";
  const tableItems = only === "table" ? items : (items || []).slice(0, 40);
  const tot = (f) => (items || []).reduce((a, it) => a + (Number(f(it)) || 0), 0);
  const title = only === "curve" ? ["KURVA S PROYEK", "项目S曲线"] : only === "table" ? ["TABEL DELIVERY LIST", "交付清单表"] : ["LAPORAN PROYEK", "项目报告"];
  return (
    <div className="print-sheet bg-white text-black text-[10px] leading-snug" data-testid="project-report-sheet">
      <div className="flex items-center justify-between border-b-2 border-black pb-2">
        <div className="flex items-center gap-3">
          <LogoMark size={40} />
          <div>
            <div className="font-display text-[14px] font-black tracking-wide">{companyName || "PT HONGLU BAJA INDONESIA"}</div>
            <div className="cn text-[10px] text-gray-600">宏睿钢印印尼有限公司 · Steel Fabrication</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-[15px] font-black">{title[0]} <span className="cn font-normal">{title[1]}</span></div>
          <div className="font-mono text-[10px] text-gray-600">Dicetak {new Date().toLocaleString("id-ID")}</div>
        </div>
      </div>

      <table className="mt-3 w-full">
        <tbody>
          <tr>
            <Cell cls="w-[18%] bg-gray-100 font-bold">Kode Proyek / 项目编号</Cell><Cell cls="w-[32%] font-mono">{project.kode}</Cell>
            <Cell cls="w-[18%] bg-gray-100 font-bold">Tonase Kontrak / 合同吨位</Cell><Cell cls="font-mono">{fmtTon(project.targetTon)} Ton</Cell>
          </tr>
          <tr>
            <Cell cls="bg-gray-100 font-bold">Nama Proyek / 项目名称</Cell><Cell>{project.nama} {project.tipe && <span className="cn text-gray-600">{project.tipe}</span>}</Cell>
            <Cell cls="bg-gray-100 font-bold">Status / 状态</Cell><Cell>{project.status}</Cell>
          </tr>
          <tr>
            <Cell cls="bg-gray-100 font-bold">Tgl Gambar Turun / 图纸下发</Cell><Cell className="font-mono">{dt(project.tanggalGambarTurun)}</Cell>
            <Cell cls="bg-gray-100 font-bold">Target Penyelesaian / 目标完成</Cell><Cell className="font-mono">{dt(project.targetPenyelesaian)}</Cell>
          </tr>
        </tbody>
      </table>

      {/* Kurva S */}
      {showCurve && <>
      <div className="mt-4 flex items-center justify-between">
        <div className="font-display text-[12px] font-bold">{only ? "" : "1. "}Kurva S — Rencana vs Realisasi STB <span className="cn font-normal text-gray-600">S曲线</span></div>
        {stats && <div className="font-mono text-[9px] text-gray-600">Mode rencana: {stats.mode === "custom" ? "Kustom (bulanan admin)" : "Otomatis (kurva S)"}</div>}
      </div>
      {!stats ? <div className="mt-1 rounded border border-gray-400 p-3 text-center text-gray-600">Kurva S belum tersedia — isi Tanggal Gambar Turun & Target Penyelesaian.</div> : (
        <>
          <div className="mt-1 grid grid-cols-4 gap-2">
            {[["Rencana s/d hari ini", `${fmtTon(stats.planNow)} T`, `${stats.elapsedPct}% waktu`], ["Realisasi STB", `${fmtTon(stats.actualNow)} T`, `${stats.progressPct}% kontrak`],
              ["Deviasi", `${stats.deviation >= 0 ? "+" : ""}${fmtTon(stats.deviation)} T`, stats.deviation < 0 ? "TERTINGGAL" : "SESUAI / DI ATAS RENCANA"], ["Sisa Waktu", `${(stats.points.length && Math.ceil((new Date(stats.end) - new Date()) / 86400000))} hari`, `s/d ${dt(stats.end)}`]].map(([l, v, sub]) => (
              <div key={l} className="rounded border border-gray-400 p-1.5"><div className="text-[8px] uppercase text-gray-600">{l}</div><div className="font-mono text-[13px] font-bold">{v}</div><div className="text-[8px] text-gray-600">{sub}</div></div>
            ))}
          </div>
          <div className="mt-2 h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={stats.points} margin={{ top: 6, right: 8, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                <XAxis dataKey="label" tick={{ fontSize: 8, fill: "#333" }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis tick={{ fontSize: 8, fill: "#333" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 9 }} />
                <Area type="monotone" dataKey="plan" name="Rencana Kumulatif (Ton)" stroke="#b45309" strokeDasharray="5 3" fill="#fde68a" fillOpacity={0.5} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="actual" name="Realisasi STB (Ton)" stroke="#0e7490" strokeWidth={2} dot={{ r: 1.5 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <table className="mt-2 w-full">
            <Head cols={["Bulan", "Rencana (T)", "Rencana Kum. (T)", "Realisasi STB (T)", "Realisasi Kum. (T)", "Deviasi Kum. (T)"]} />
            <tbody>
              {monthTable.map((m) => (
                <tr key={m.bulan}>
                  <Cell>{monthLabel(m.bulan)}</Cell><Cell cls="font-mono text-right">{fmtTon(m.plan)}</Cell><Cell cls="font-mono text-right">{fmtTon(m.accPlan)}</Cell>
                  <Cell cls="font-mono text-right">{fmtTon(m.actual)}</Cell><Cell cls="font-mono text-right">{fmtTon(m.accAct)}</Cell>
                  <Cell cls={`font-mono text-right ${m.accAct - m.accPlan < 0 ? "text-red-700" : "text-green-700"}`}>{m.accAct - m.accPlan >= 0 ? "+" : ""}{fmtTon(m.accAct - m.accPlan)}</Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      </>}

      {/* Delivery list */}
      {showTable && <>
      <div className="mt-4 font-display text-[12px] font-bold">{only ? "" : "2. "}Tabel Delivery List <span className="cn font-normal text-gray-600">交付清单</span>{filterLabel && <span className="ml-2 font-mono text-[9px] font-normal text-gray-600">{filterLabel}</span>}</div>
      <div className="mt-1 grid grid-cols-6 gap-2">
        {[["Item Marking", tot(() => 1)], ["Total Qty", `${tot((it) => it.qty)} pcs`], ["Qty Selesai (STB)", `${tot((it) => it.qtySelesai)} pcs`], ["Qty Belum Selesai", `${tot((it) => it.qtySisa ?? it.qty)} pcs`], ["Tonase DL", `${fmtTon(tot((it) => it.totalBerat) / 1000)} T`], ["Total Sisa Tonase", `${fmtTon(tot((it) => it.beratSisa ?? it.totalBerat) / 1000)} T`]].map(([l, v]) => (
          <div key={l} className="rounded border border-gray-400 p-1.5"><div className="text-[8px] uppercase text-gray-600">{l}</div><div className="font-mono text-[13px] font-bold">{v}</div></div>
        ))}
      </div>
      {items?.length > 0 && (
        <table className="mt-2 w-full" data-testid="project-report-dl-table">
          <Head cols={["No", "Marking", "Profile", "Qty", "Unit (kg)", "Total (kg)", "Vendor", "Qty Selesai", "Qty Belum Selesai", "Sisa Tonase (kg)"]} />
          <tbody>
            {tableItems.map((it, i) => (
              <tr key={it.id}><Cell>{i + 1}</Cell><Cell cls="font-mono">{it.marking}</Cell><Cell>{it.profile || "—"}</Cell><Cell cls="font-mono text-right">{it.qty}</Cell><Cell cls="font-mono text-right">{kg(it.unitBerat)}</Cell><Cell cls="font-mono text-right">{kg(it.totalBerat)}</Cell>
                <Cell>{it.vendorName || <span className="text-gray-500">Belum SPK</span>}</Cell><Cell cls="font-mono text-right">{it.qtySelesai ?? 0}</Cell><Cell cls={`font-mono text-right ${(it.qtySisa ?? it.qty) > 0 ? "text-red-700" : "text-green-700"}`}>{it.qtySisa ?? it.qty}</Cell><Cell cls="font-mono text-right">{kg(it.beratSisa ?? it.totalBerat)}</Cell></tr>
            ))}
            {items.length > tableItems.length && <tr><Cell cls="text-center text-gray-600" colSpan={10}>… {items.length - tableItems.length} item lainnya (cetak Tabel Delivery List untuk lengkap)</Cell></tr>}
          </tbody>
          <tfoot>
            <tr className="bg-gray-100 font-bold">
              <Cell cls="text-right" colSpan={3}>TOTAL / 合计</Cell><Cell cls="font-mono text-right">{tot((it) => it.qty)}</Cell><Cell /><Cell cls="font-mono text-right">{kg(tot((it) => it.totalBerat))}</Cell><Cell />
              <Cell cls="font-mono text-right">{tot((it) => it.qtySelesai)}</Cell><Cell cls="font-mono text-right text-red-700">{tot((it) => it.qtySisa ?? it.qty)}</Cell><Cell cls="font-mono text-right" data-testid="project-report-sisa-tonase">{kg(tot((it) => it.beratSisa ?? it.totalBerat))} kg = {fmtTon(tot((it) => it.beratSisa ?? it.totalBerat) / 1000)} T</Cell>
            </tr>
          </tfoot>
        </table>
      )}
      </>}

      {/* SPK */}
      {showCurve && <>
      <div className="mt-4 font-display text-[12px] font-bold">{only ? "" : "3. "}Daftar SPK <span className="cn font-normal text-gray-600">派工单列表</span> <span className="font-mono text-[9px] font-normal text-gray-600">({spks.length} SPK · {fmtTon(spks.reduce((a, s) => a + (s.totalTon || 0), 0))} Ton)</span></div>
      {spks.length === 0 ? <div className="mt-1 rounded border border-gray-400 p-2 text-center text-gray-600">Belum ada SPK untuk proyek ini</div> : (
        <table className="mt-1 w-full">
          <Head cols={["No SPK", "Tanggal", "Vendor", "Area", "Qty", "Tonase (T)", "Target Selesai", "Status"]} />
          <tbody>
            {spks.map((s) => (
              <tr key={s.id}><Cell cls="font-mono">{s.no}</Cell><Cell cls="font-mono">{s.tanggal}</Cell><Cell>{s.vendorName}{s.vendorSingkatan ? ` (${s.vendorSingkatan})` : ""}</Cell><Cell>{s.area}</Cell><Cell cls="font-mono text-right">{s.qty}</Cell><Cell cls="font-mono text-right">{fmtTon(s.totalTon)}</Cell><Cell cls="font-mono">{s.tanggalSelesai || "—"}</Cell><Cell>{s.status}</Cell></tr>
            ))}
          </tbody>
        </table>
      )}
      </>}

      <div className="mt-6 grid grid-cols-3 gap-6 text-center">
        {["Dibuat / 制表", "Diperiksa / 审核", "Disetujui / 批准"].map((t) => (
          <div key={t} className="sig-block leading-relaxed"><div className="font-bold">{t}</div><div className="h-14" /><div className="border-t border-black pt-1 text-gray-600">( ________________ )</div></div>
        ))}
      </div>
    </div>
  );
}

function BundleCover({ bundle }) {
  const rows = bundle.reports;
  const tot = (f) => rows.reduce((a, r) => a + (f(r) || 0), 0);
  const behind = rows.filter((r) => r.curve?.ok && r.curve.deviation < 0).length;
  return (
    <div className="print-sheet bg-white text-black text-[10px] leading-snug" data-testid="bundle-cover-sheet">
      <div className="flex items-center justify-between border-b-2 border-black pb-2">
        <div className="flex items-center gap-3">
          <LogoMark size={40} />
          <div>
            <div className="font-display text-[14px] font-black tracking-wide">{bundle.companyName || "PT HONGLU BAJA INDONESIA"}</div>
            <div className="cn text-[10px] text-gray-600">宏睿钢印印尼有限公司 · Steel Fabrication</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-[15px] font-black">LAPORAN BULANAN PROYEK <span className="cn font-normal">项目月报</span></div>
          <div className="font-mono text-[10px] text-gray-600">{rows.length} proyek aktif · Dicetak {new Date().toLocaleString("id-ID")}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-5 gap-2">
        {[["Proyek Aktif", rows.length], ["Total Kontrak", `${fmtTon(tot((r) => r.project.targetTon))} T`], ["Total Delivery List", `${fmtTon(tot((r) => r.summary.totalTon))} T`],
          ["Total Realisasi STB", `${fmtTon(tot((r) => r.curve?.ok ? r.curve.actualNow : 0))} T`], ["Proyek Tertinggal", `${behind} / ${rows.filter((r) => r.curve?.ok).length}`]].map(([l, v]) => (
          <div key={l} className="rounded border border-gray-400 p-1.5"><div className="text-[8px] uppercase text-gray-600">{l}</div><div className="font-mono text-[13px] font-bold">{v}</div></div>
        ))}
      </div>
      <div className="mt-4 font-display text-[12px] font-bold">Ringkasan Semua Proyek <span className="cn font-normal text-gray-600">项目总览</span></div>
      <table className="mt-1 w-full">
        <Head cols={["No", "Kode", "Nama Proyek", "Kontrak (T)", "DL (T)", "STB (T)", "Rencana (T)", "Deviasi (T)", "Target", "Sisa Hari", "Status"]} />
        <tbody>
          {rows.map((r, i) => {
            const c = r.curve?.ok ? r.curve : null;
            const dev = c ? c.deviation : null;
            return (
              <tr key={r.project.id} className={dev != null && dev < 0 ? "bg-red-50" : ""}>
                <Cell>{i + 1}</Cell><Cell cls="font-mono">{r.project.kode}</Cell><Cell>{r.project.nama}</Cell>
                <Cell cls="font-mono text-right">{fmtTon(r.project.targetTon)}</Cell><Cell cls="font-mono text-right">{fmtTon(r.summary.totalTon)}</Cell>
                <Cell cls="font-mono text-right">{c ? fmtTon(c.actualNow) : fmtTon(r.project.stbTon || 0)}</Cell><Cell cls="font-mono text-right">{c ? fmtTon(c.planNow) : "—"}</Cell>
                <Cell cls={`font-mono text-right font-bold ${dev == null ? "text-gray-500" : dev < 0 ? "text-red-700" : "text-green-700"}`}>{dev == null ? "—" : `${dev >= 0 ? "+" : ""}${fmtTon(dev)}`}</Cell>
                <Cell cls="font-mono">{dt(r.project.targetPenyelesaian)}</Cell>
                <Cell cls={`font-mono text-right ${c && c.daysLeft < 0 ? "text-red-700" : ""}`}>{c ? c.daysLeft : "—"}</Cell><Cell>{r.project.status}</Cell>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 text-[9px] text-gray-600">Baris merah = realisasi STB di bawah rencana. "—" = proyek belum memiliki tanggal gambar turun / target penyelesaian. Halaman berikutnya: laporan detail tiap proyek.</div>
      <div className="mt-8 grid grid-cols-3 gap-6 text-center">
        {["Dibuat / 制表", "Diperiksa / 审核", "Disetujui / 批准"].map((t) => (
          <div key={t} className="sig-block leading-relaxed"><div className="font-bold">{t}</div><div className="h-14" /><div className="border-t border-black pt-1 text-gray-600">( ________________ )</div></div>
        ))}
      </div>
    </div>
  );
}

export function ProjectBundleOverlay({ onClose, bundle }) {
  return (
    <PrintOverlay testid="project-bundle-overlay" onClose={onClose}>
      <div className="space-y-4 print:space-y-0" data-testid="project-bundle-sheets">
        <BundleCover bundle={bundle} />
        {bundle.reports.map((r) => (
          <ProjectReportDoc key={r.project.id} project={r.project} summary={r.summary} curve={r.curve} spks={r.spks} items={r.items} companyName={bundle.companyName} />
        ))}
      </div>
    </PrintOverlay>
  );
}

export function ProjectReportOverlay({ onClose, ...props }) {
  return (
    <PrintOverlay testid="project-report-overlay" onClose={onClose}>
      <ProjectReportDoc {...props} />
    </PrintOverlay>
  );
}
